import { useEffect, useState } from 'react';
import { Badge, Button, Card, Form, Stack } from 'react-bootstrap';
import { httpsCallable } from 'firebase/functions';
import { deleteField, doc as firebaseDoc, updateDoc } from 'firebase/firestore';

import { db, firebaseFunctions } from '../../auth/firebase-auth';
import type { TikTokAccountRecord } from '../../pages/home';

type CategoryOption =
  | 'motivation'
  | 'fitness'
  | 'forex'
  | 'travel'
  | 'relationship'
  | 'generic'
  | 'interview_prep';

const CATEGORY_OPTIONS: CategoryOption[] = [
  'motivation',
  'fitness',
  'forex',
  'travel',
  'relationship',
  'generic',
  'interview_prep'
];

function ConnectedAccounts(props: {
  accounts: TikTokAccountRecord[];
  onAccountChanged: () => void;
}) {
  const [categoryByDocId, setCategoryByDocId] = useState<Record<string, string>>(
    {}
  );

  useEffect(() => {
    const nextState: Record<string, string> = {};
    props.accounts.forEach((account) => {
      nextState[account.id] = account.tiktok_account_category || 'generic';
    });
    setCategoryByDocId(nextState);
  }, [props.accounts]);

  const activeAccounts = props.accounts.filter((account) => {
    const hasTikTok = Boolean(account.connected_accounts?.tiktok?.account_data);
    return account.account_status === 'active' && hasTikTok;
  });

  return (
    <Card style={{ width: '100%' }}>
      <Card.Body>
        <Stack
          direction="horizontal"
          className="justify-content-between align-items-center mb-3"
        >
          <div>
            <Card.Title style={{ marginBottom: '0.25rem' }}>
              TikTok Accounts
            </Card.Title>
            <Card.Text style={{ marginBottom: 0 }}>
              Connected: <b>{activeAccounts.length}</b>
            </Card.Text>
          </div>
          <Button variant="primary" onClick={requestToken}>
            Add TikTok account
          </Button>
        </Stack>

        {activeAccounts.length === 0 && (
          <Card.Text>No TikTok account connected yet.</Card.Text>
        )}

        <Stack gap={3}>
          {activeAccounts.map((account) => {
            const creatorData =
              account.connected_accounts?.tiktok?.account_data?.user_data;
            const displayName =
              creatorData?.creator_nickname ||
              creatorData?.creator_username ||
              account.email;
            const creatorUsername = creatorData?.creator_username || '';
            const avatarUrl = creatorData?.creator_avatar_url || '';
            const category = categoryByDocId[account.id] || 'generic';

            return (
              <div
                key={account.id}
                style={{
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  padding: '12px'
                }}
              >
                <Stack
                  direction="horizontal"
                  className="justify-content-between align-items-start"
                >
                  <Stack direction="horizontal" gap={2}>
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={displayName}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : null}
                    <div>
                      <div style={{ fontWeight: 600 }}>{displayName}</div>
                      <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                        {creatorUsername ? `@${creatorUsername}` : account.email}
                      </div>
                    </div>
                  </Stack>
                  <Badge pill bg="success">
                    active
                  </Badge>
                </Stack>

                <Form.Group className="mt-3">
                  <Form.Label style={{ marginBottom: '0.25rem' }}>
                    Content category
                  </Form.Label>
                  <Form.Select
                    value={category}
                    onChange={(event) => {
                      const selected = event.target.value;
                      setCategoryByDocId((prev) => ({
                        ...prev,
                        [account.id]: selected
                      }));
                    }}
                  >
                    {CATEGORY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Stack direction="horizontal" gap={2} className="mt-3">
                  <Button
                    variant="primary"
                    onClick={async () => {
                      await updateTikTokAccountCategoryByDocId(
                        account.id,
                        category
                      );
                      props.onAccountChanged();
                    }}
                  >
                    Update category
                  </Button>
                  <Button
                    variant="danger"
                    onClick={async () => {
                      await disconnectAccountByDocId(account.id);
                      props.onAccountChanged();
                    }}
                  >
                    Disconnect
                  </Button>
                </Stack>
              </div>
            );
          })}
        </Stack>
      </Card.Body>
    </Card>
  );
}

const getOauthURL = httpsCallable<{}, { url: string }>(
  firebaseFunctions,
  'getOauthURL'
);

const requestToken = async () => {
  getOauthURL()
    .then((result) => {
      window.location.href = result.data.url;
    })
    .catch((error) => {
      const code = error.code;
      const message = error.message;
      const details = error.details;
      console.log(code, message, details);
    });
};

async function disconnectAccountByDocId(docId: string) {
  const collectionName = 'tiktok_automation_users';
  const docRef = firebaseDoc(db, collectionName, docId);
  await updateDoc(docRef, {
    'connected_accounts.tiktok': deleteField(),
    account_status: 'disabled'
  });
}

async function updateTikTokAccountCategoryByDocId(
  docId: string,
  category: string
) {
  const collectionName = 'tiktok_automation_users';
  const docRef = firebaseDoc(db, collectionName, docId);
  await updateDoc(docRef, {
    tiktok_account_category: category
  });
}

export default ConnectedAccounts;
